import { API_BASE_URL } from '../config/api';

export interface Notification {
  id: string;
  message: string;
  type: string;
  timestamp: string;
  acknowledged: boolean;
  authorizedUserId: string;
  actionUrl?: string | null;
}

export interface CreateNotificationRequest {
  userId: string;
  message: string;
  type: string;
  actionUrl?: string | null;
}

export interface PagedNotificationResult {
  items: Notification[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export const notificationApi = {
  /**
   * Get notifications with optional filtering
   * @param acknowledged - Filter by acknowledged status (optional)
   * @param page - Page number (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   */
  getNotifications: async (
    acknowledged?: boolean,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PagedNotificationResult> => {
    const params = new URLSearchParams();
    if (acknowledged !== undefined) {
      params.append('acknowledged', acknowledged.toString());
    }
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    const response = await fetch(`${API_BASE_URL}/api/Notification?${params}`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notifications');
    }

    return response.json();
  },

  /**
   * Get a specific notification by ID
   * @param id - Notification UUID
   */
  getNotificationById: async (id: string): Promise<Notification> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification/${id}`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notification');
    }

    return response.json();
  },

  /**
   * Create a new notification
   * @param request - Notification creation data
   */
  createNotification: async (request: CreateNotificationRequest): Promise<Notification> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to create notification');
    }

    return response.json();
  },

  /**
   * Delete a specific notification
   * @param id - Notification UUID
   */
  deleteNotification: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification/${id}`, {
      method: 'DELETE',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete notification');
    }
  },

  /**
   * Mark a specific notification as acknowledged
   * @param id - Notification UUID
   */
  acknowledgeNotification: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification/${id}/acknowledge`, {
      method: 'PATCH',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to acknowledge notification');
    }
  },

  /**
   * Mark all notifications as acknowledged
   * @returns The count of notifications that were acknowledged
   */
  acknowledgeAllNotifications: async (): Promise<number> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification/acknowledge-all`, {
      method: 'PATCH',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to acknowledge all notifications');
    }

    return response.json();
  },

  /**
   * Delete all notifications
   * @returns The count of notifications that were deleted
   */
  deleteAllNotifications: async (): Promise<number> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification/delete-all`, {
      method: 'DELETE',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete all notifications');
    }

    return response.json();
  },

  /**
   * Get the count of unacknowledged notifications
   * @returns The count of unacknowledged notifications
   */
  getUnacknowledgedCount: async (): Promise<number> => {
    const response = await fetch(`${API_BASE_URL}/api/Notification/count/unacknowledged`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch unacknowledged notification count');
    }

    return response.json();
  },
};
